import { useContext, useEffect, useRef, useState } from "react";
import BreadCrumb from "../../components/common/BreadCrumb";
import PaginationLayout from "../../components/common/PaginationLayout";
import { GlobalContext, handleError } from "../../context/Provider";
import Spinner from "../../components/common/spinner";
import henceforthApi from "../../utils/henceforthApi";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";

// const LIMIT = 10;

const Store = () => {
    const breadCrumbPath = [
        { name: `Home`, url: `/`, active: "" },
        { name: `Store Listing`, url: ``, active: "not-allowed" }
    ];

    const router   = useNavigate();
    const location = useLocation();

    const newParam = new URLSearchParams(location.search);

    const { language_data, loading, setLoading, authState, authDispatch } = useContext(GlobalContext);

    const [page, setPage] = useState<number>(Number(newParam.get("page")) || 1);

    const [storeL, setStoreL] = useState<{ total_count: number; data: any[] }>({
        total_count: 0,
        data: []
    });

    // Bulk upload state
    const [bulkModal,  setBulkModal]  = useState(false);
    const [bulkFile,   setBulkFile]   = useState<File | null>(null);
    const [uploading,  setUploading]  = useState(false);
    const fileInputRef                = useRef<HTMLInputElement>(null);

    // ─── Fetch ────────────────────────────────────────────────────────────────
    const getStoreList = async (currentPage: number) => {
        setLoading(true);
        try {
            const res = (
                await henceforthApi.Store.getStoreList(
                    // currentPage - 1,
                    // LIMIT,
                    newParam.toString()
                )
            ).data;

            setStoreL({
                total_count: res.total_count,
                data: res.data
            });
        } catch (err: any) {
            setStoreL({ total_count: 0, data: [] });
            handleError(err, "active", authDispatch);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const currentPage = Number(newParam.get("page")) || 1;
        setPage(currentPage);
        getStoreList(currentPage);
    }, [
        newParam.get("page"),
        newParam.get("search")
    ]);

    // ─── Search ───────────────────────────────────────────────────────────────
    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trim();
        const params = new URLSearchParams(location.search);
        if (value) {
            params.set("search", value);
        } else {
            params.delete("search");
        }
        params.set("page", "1");
        setPage(1);
        router(`/store?${params.toString()}`);
    };

    // ─── Page change ──────────────────────────────────────────────────────────
    const handlePageChange = (newPage: number) => {
        const params = new URLSearchParams(location.search);
        params.set("page", String(newPage));
        setPage(newPage);
        router(`/store?${params.toString()}`);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // ─── Bulk upload ──────────────────────────────────────────────────────────
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]) setBulkFile(e.target.files[0]);
    };

    const handleBulkUpload = async () => {
        if (!bulkFile) return toast.error("Please select a CSV file");
        if (!bulkFile.name.endsWith(".csv") && !bulkFile.type.includes("csv"))
            return toast.error("Only CSV files are allowed");

        const fd = new FormData();
        fd.append("file", bulkFile);
        setUploading(true);
        try {
            const res = await henceforthApi.Store.addBulkStore(authState?._id, fd);
            toast.success(res.data.message || "Bulk upload successful");
            getStoreList(1);
            closeBulkModal();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Bulk upload failed");
        } finally {
            setUploading(false);
        }
    };

    const closeBulkModal = () => {
        setBulkModal(false);
        setBulkFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div>
            <BreadCrumb pathNameDeclare={breadCrumbPath} />

            <div className="page-spacing">
                <section className="products">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="common-card">

                                    {/* ── Header ── */}
                                    <div className="common-card-title d-flex justify-content-between align-items-center flex-column flex-sm-row gap-2">
                                        <h5>
                                            {language_data?.[authState.lang]?.common_store || "Store"}
                                        </h5>
                                        <div className="d-flex gap-2 flex-wrap align-items-center">

                                            {/* Search */}
                                            <div className="input-group input-group-sm" style={{ width: 240 }}>
                                                <span className="input-group-text">
                                                    <i className="fa fa-search" />
                                                </span>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Search name, email, company..."
                                                    defaultValue={newParam.get("search") ?? ""}
                                                    onChange={handleSearch}
                                                />
                                            </div>

                                            {/* Download Sample */}
                                            <button
                                                className="btn btn-white btn-sm"
                                                onClick={() => {
                                                    const link = document.createElement("a");
                                                    link.href = `${process.env.PUBLIC_URL}/assets/csv/Store.csv`;
                                                    link.download = "Store.csv";
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                }}
                                            >
                                                <i className="fa fa-download me-1" />
                                                {language_data?.[authState.lang]?.common_download_sample || "Download Sample"}
                                            </button>

                                            {/* Add Store */}
                                            <button
                                                className="btn btn-theme btn-sm"
                                                onClick={() => router("/store/add")}
                                            >
                                                {language_data?.[authState.lang]?.store_add_store || "Add Store"}
                                            </button>

                                            {/* Bulk Upload */}
                                            <button
                                                className="btn btn-white btn-sm"
                                                onClick={() => setBulkModal(true)}
                                            >
                                                <i className="fa fa-upload me-1" />
                                                {language_data?.[authState.lang]?.common_bulk_upload || "Bulk Upload"}
                                            </button>
                                        </div>
                                    </div>

                                    {/* ── Table ── */}
                                    <div className="common-card-content">
                                        <div className="data-list-table table-responsive mb-4 text-center">
                                            {loading ? (
                                                <Spinner color="text-success" />
                                            ) : (
                                                <table className="table table-striped align-middle">
                                                    <thead>
                                                        <tr>
                                                            <th>{language_data?.[authState.lang]?.common_sr_no}.</th>
                                                            <th>{language_data?.[authState.lang]?.common_name}</th>
                                                            <th>{language_data?.[authState.lang]?.common_email}</th>
                                                            <th>{language_data?.[authState.lang]?.common_company_name}</th>
                                                            <th>{language_data?.[authState.lang]?.common_phone_number}</th>
                                                            <th>{language_data?.[authState.lang]?.common_address}</th>
                                                            <th>{language_data?.[authState.lang]?.common_actions || "Actions"}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {Array.isArray(storeL.data) && storeL.data.length ? (
                                                            storeL.data.map((item: any, index: number) => (
                                                                <tr key={item._id}>
                                                                    <td>{(index + 1)}</td>
                                                                    <td>{item.name || language_data?.[authState.lang]?.common_not_available}</td>
                                                                    <td>{item.email || language_data?.[authState.lang]?.common_not_available}</td>
                                                                    <td>{item.company || "—"}</td>
                                                                    <td>{`+${item.country_code} ${item.phone_number}`}</td>
                                                                    <td>{item.full_address || language_data?.[authState.lang]?.common_not_available}</td>
                                                                    <td>
                                                                        <button
                                                                            className="btn btn-sm btn-theme"
                                                                            onClick={() => router(`/store/${item._id}`)}
                                                                        >
                                                                            <i className="fa fa-eye me-1" />
                                                                            {language_data?.[authState.lang]?.common_view || "View"}
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan={7}>
                                                                    {language_data?.[authState.lang]?.common_no_data_found || "No data found"}
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>

                                       
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {/* ── Bulk Upload Modal ── */}
            {bulkModal && (
                <div
                    className="modal fade show"
                    style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {language_data?.[authState.lang]?.common_bulk_upload || "Bulk Upload Stores"}
                                </h5>
                                <button type="button" className="btn-close" onClick={closeBulkModal} />
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">
                                        {language_data?.[authState.lang]?.common_select_csv || "Select CSV File"}
                                    </label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept=".csv"
                                        onChange={handleFileChange}
                                        ref={fileInputRef}
                                    />
                                    <small className="text-muted">
                                        Only CSV files are allowed. The file must contain required columns.
                                    </small>
                                </div>
                                {bulkFile && (
                                    <div className="alert alert-info">
                                        <i className="fa fa-file-text-o me-2" />
                                        {bulkFile.name} ({(bulkFile.size / 1024).toFixed(2)} KB)
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={closeBulkModal}>
                                    {language_data?.[authState.lang]?.common_cancel || "Cancel"}
                                </button>
                                <button
                                    className="btn btn-theme"
                                    onClick={handleBulkUpload}
                                    disabled={uploading || !bulkFile}
                                >
                                    {uploading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" />
                                            {language_data?.[authState.lang]?.common_uploading || "Uploading..."}
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa fa-upload me-1" />
                                            {language_data?.[authState.lang]?.common_upload || "Upload"}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Store;